/**
 * Single source of truth serializer for Survey definition dirty-state.
 * Mirrors the v2.15.6 serializer pattern (Cards, Locations, Organizations, Employees).
 */

const _toStr = (v, d = '') => (v === null || v === undefined ? d : String(v).trim());
const _toBool = (v) => Boolean(v);
const _toInt = (v, d = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
};

const FIELDS = [
    { key: 'slug',    src: 'slug',     state: 'slug',     type: 'str' },
    { key: 'enabled', src: 'enabled',  state: 'enabled',  type: 'bool' },
    { key: 'name_en', src: ['name','en'],        state: ['name','en'],        type: 'str' },
    { key: 'name_fr', src: ['name','fr'],        state: ['name','fr'],        type: 'str' },
    { key: 'name_es', src: ['name','es'],        state: ['name','es'],        type: 'str' },
    { key: 'desc_en', src: ['description','en'], state: ['description','en'], type: 'str' },
    { key: 'desc_fr', src: ['description','fr'], state: ['description','fr'], type: 'str' },
    { key: 'desc_es', src: ['description','es'], state: ['description','es'], type: 'str' },
    { key: 'location_id',          src: 'location_id',          state: 'location_id',          type: 'str' },
    { key: 'low_rating_threshold', src: ['alert_rules','low_rating_threshold'], state: ['alert_rules','low_rating_threshold'], type: 'int' },
    { key: 'trigger_on_low_rating',    src: ['alert_rules','trigger_on_low_rating'],    state: ['alert_rules','trigger_on_low_rating'],    type: 'bool' },
    { key: 'trigger_on_negative_choice', src: ['alert_rules','trigger_on_negative_choice'], state: ['alert_rules','trigger_on_negative_choice'], type: 'bool' },
    // v3.5.0 — Appearance / branding fields. Included so that uploads, theme
    // changes, and background image edits register as dirty state.
    { key: 'logo_url',             src: 'logo_url',             state: 'logo_url',             type: 'str' },
    { key: 'logo_size',            src: 'logo_size',            state: 'logo_size',            type: 'str' },
    { key: 'display_mode',         src: 'display_mode',         state: 'display_mode',         type: 'str' },
    { key: 'background_image_url', src: 'background_image_url', state: 'background_image_url', type: 'str' },
    { key: 'card_style',           src: 'card_style',           state: 'card_style',           type: 'str' },
    // P_SURVEY_EDITOR_UX_ENHANCEMENTS_1.0 — Overall Experience toggle.
    // Treated as bool with default true (undefined → true).
    { key: 'overall_experience_enabled', src: 'overall_experience_enabled', state: 'overall_experience_enabled', type: 'bool_default_true' },
];

function getPath(obj, path) {
    if (!obj) return undefined;
    if (Array.isArray(path)) {
        let cur = obj;
        for (const p of path) {
            if (cur == null) return undefined;
            cur = cur[p];
        }
        return cur;
    }
    return obj[path];
}

function normalize(v, type) {
    switch (type) {
        case 'str':  return _toStr(v);
        case 'bool': return _toBool(v);
        case 'bool_default_true': return v === undefined || v === null ? true : _toBool(v);
        case 'int':  return _toInt(v);
        default:     return v ?? '';
    }
}

// P_SURVEY_CLOSING_DIRTY_1.0 — the Closing Page tab (Gift from us, Scratch &
// Win, closing title/message, Google Review) lives entirely inside
// `closing_page`, which was absent from this serializer, so nothing done on
// that tab ever enabled Save. Canonicalised so an UNSET closing_page and an
// empty one serialize identically, key order never matters, and the
// server-owned `gift.issued_count` is ignored.
function canonicalClosingPage(value) {
    const clean = (v) => {
        if (Array.isArray(v)) {
            const arr = v.map(clean).filter((x) => x !== undefined);
            return arr.length ? arr : undefined;
        }
        if (v && typeof v === 'object') {
            const out = {};
            for (const key of Object.keys(v).sort()) {
                if (key === 'issued_count') continue;   // server-owned counter
                const cleaned = clean(v[key]);
                if (cleaned !== undefined) out[key] = cleaned;
            }
            return Object.keys(out).length ? out : undefined;
        }
        if (v === null || v === undefined || v === '') return undefined;
        return v;                                       // false and 0 are kept
    };
    return JSON.stringify(clean(value) ?? {});
}

export function serializeSurveyDefinition(input, source = 'survey') {
    if (!input || typeof input !== 'object') return JSON.stringify({});
    const out = {};
    for (const f of FIELDS) {
        const path = source === 'survey' ? f.src : f.state;
        out[f.key] = normalize(getPath(input, path), f.type);
    }
    // Questions and recipients hash separately as JSON blobs.
    out.questions = JSON.stringify(input.questions || []);
    out.notification_emails = JSON.stringify(input.notification_emails || []);
    out.notification_phones = JSON.stringify(input.notification_phones || []);
    out.closing_page = canonicalClosingPage(input.closing_page);
    // enabled_languages — null represents "inherit from org"; preserve that distinctly.
    out.enabled_languages = input.enabled_languages === null || input.enabled_languages === undefined
        ? 'inherit'
        : JSON.stringify(input.enabled_languages);
    return JSON.stringify(out);
}

export default serializeSurveyDefinition;
